import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse, PaginatedResult, PaginationParams } from '@/types/api'

export type { ApiResponse, PaginatedResult, PaginationParams }

// ── Axios Instance ───────────────────────────────────────────────────────────

export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5001/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000
})

// ── Request Interceptor ──────────────────────────────────────────────────────

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// ── Response Interceptor (401 refresh with queue) ────────────────────────────

let isRefreshing = false
let failedQueue: {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}[] = []

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token)
    else reject(error)
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }

    // Don't intercept auth endpoints — let the calling code handle errors
    if (originalRequest.url?.toLowerCase().startsWith('/auth/')) {
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`
        return apiClient(originalRequest)
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    const refreshToken = localStorage.getItem('refreshToken')

    if (!refreshToken) {
      isRefreshing = false
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      window.location.href = '/login'
      return Promise.reject(error)
    }

    try {
      const { data } = await apiClient.post('/auth/refresh', { refreshToken })
      const newAccessToken: string = data.result.token
      const newRefreshToken: string = data.result.refreshToken

      localStorage.setItem('accessToken', newAccessToken)
      localStorage.setItem('refreshToken', newRefreshToken)

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      processQueue(null, newAccessToken)

      return apiClient(originalRequest)
    } catch (refreshError) {
      processQueue(refreshError, null)
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
      window.location.href = '/login'
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

// ── BaseService ──────────────────────────────────────────────────────────────

export class BaseService<T, TCreate = Partial<T>, TUpdate = Partial<T>> {
  constructor(private readonly endpoint: string) {}

  async getAll(params?: PaginationParams): Promise<ApiResponse<PaginatedResult<T>>> {
    const { data } = await apiClient.get<ApiResponse<PaginatedResult<T>>>(this.endpoint, {
      params
    })
    return data
  }

  async getById(id: string): Promise<ApiResponse<T>> {
    const { data } = await apiClient.get<ApiResponse<T>>(`${this.endpoint}/${id}`)
    return data
  }

  async create(payload: TCreate): Promise<ApiResponse<T>> {
    const { data } = await apiClient.post<ApiResponse<T>>(this.endpoint, payload)
    return data
  }

  async update(
    id: string,
    payload: TUpdate,
    method: 'patch' | 'put' = 'patch'
  ): Promise<ApiResponse<T>> {
    const { data } = await apiClient[method]<ApiResponse<T>>(`${this.endpoint}/${id}`, payload)
    return data
  }

  async delete(id: string): Promise<ApiResponse<void>> {
    const { data } = await apiClient.delete<ApiResponse<void>>(`${this.endpoint}/${id}`)
    return data
  }
}
